// src/components/ui/QuotasListDisplay.js
import React from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

function QuotasMonitorerListDisplay({ quotasObject }) {
	const formatDate = (dateString) => {
		try {
			const date = new Date(dateString);
			return date.toLocaleString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		} catch (error) {
			return dateString;
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-lg font-semibold text-gray-800">
					Found {quotasObject.length} quota{quotasObject.length !== 1 ? 's' : ''}
				</h3>
			</div>

			{/* Quotas List */}
			<div className="space-y-4">
				{quotasObject.map((quota) => {
					const percentage = quota.target_count > 0 
						? Math.round((quota.current_count / quota.target_count) * 100) 
						: 0;
					const isComplete = quota.current_count >= quota.target_count;

					return (
						<div 
							key={quota.quota_id} 
							className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
						>
							{/* Quota Header */}
							<div className="flex items-start justify-between mb-3">
								<div className="flex-1">
									<div className="flex items-center gap-2">
										<h4 className="text-base font-semibold text-gray-900">
											{quota.label || 'Untitled Quota'}
										</h4>
										{isComplete && (
											<CheckCircle className="h-5 w-5 text-green-500" />
										)}
									</div>
									<p className="text-xs text-gray-500 mt-1">
										ID: {quota.quota_id}
									</p>
								</div>
								<div className="text-right">
									<span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
										quota.enforcement_type === 'HARD' 
											? 'bg-red-100 text-red-800' 
											: 'bg-yellow-100 text-yellow-800'
									}`}>
										{quota.enforcement_type}
									</span>
									<p className="text-xs text-gray-500 mt-1">
										Priority: {quota.priority}
									</p>
								</div>
							</div>

							{/* Quota Timestamps */}
							<div className="flex gap-4 text-xs text-gray-500 mb-3">
								<span>Created: {formatDate(quota.created_at)}</span>
								<span>Updated: {formatDate(quota.updated_at)}</span>
							</div>

							{/* Progress Bar */}
							<div className="mb-3">
								<div className="flex justify-between items-center mb-1">
									<span className="text-sm font-medium text-gray-700">
										Progress
									</span>
									<span className="text-sm font-semibold text-gray-900">
										{quota.current_count} / {quota.target_count} ({percentage}%)
									</span>
								</div>
								<div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
									<div 
										className={`h-full rounded-full transition-all duration-500 ${
											isComplete ? 'bg-green-500' : 'bg-primary'
										}`}
										style={{ width: `${Math.min(percentage, 100)}%` }}
									></div>
								</div>
							</div>

							{/* Conditions Table */}
							<div className="mt-3 pt-3 border-t border-gray-200">
								<p className="text-sm font-medium text-gray-700 mb-3">
									Conditions ({quota.conditions.length})
								</p>
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
													Field
												</th>
												<th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
													Operator
												</th>
												<th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
													Value
												</th>
												<th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
													Data Type
												</th>
												<th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
													Logic
												</th>
											</tr>
										</thead>
										<tbody className="bg-white divide-y divide-gray-200">
											{quota.conditions.map((condition, idx) => (
												<tr key={condition.condition_id} className="hover:bg-gray-50">
													<td className="px-3 py-2 text-sm font-medium text-gray-900">
														{condition.field_name}
													</td>
													<td className="px-3 py-2 text-sm text-gray-700 font-mono">
														{condition.operator}
													</td>
													<td className="px-3 py-2 text-sm text-gray-900">
														<span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
															{condition.value}
														</span>
													</td>
													<td className="px-3 py-2 text-sm text-gray-600">
														<span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
															{condition.data_type}
														</span>
													</td>
													<td className="px-3 py-2 text-sm">
														{idx < quota.conditions.length - 1 ? (
															<span className="font-semibold text-primary">
																{condition.logical_group}
															</span>
														) : (
															<span className="text-gray-400">—</span>
														)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								{/* Condition Timestamps */}
								{quota.conditions.length > 0 && (
									<div className="mt-2 text-xs text-gray-500">
										<details className="cursor-pointer">
											<summary className="hover:text-gray-700">
												View condition timestamps
											</summary>
											<div className="mt-2 space-y-1 pl-4 border-l-2 border-gray-200">
												{quota.conditions.map((condition) => (
													<div key={condition.condition_id} className="text-xs">
														<span className="font-medium">{condition.field_name}:</span>
														<span className="ml-2">Created: {formatDate(condition.created_at)}</span>
														<span className="ml-2">Updated: {formatDate(condition.updated_at)}</span>
													</div>
												))}
											</div>
										</details>
									</div>
								)}
							</div>

							{/* Parent Info */}
							{quota.parent_quota_id && (
								<div className="mt-3 pt-3 border-t border-gray-200">
									<p className="text-xs text-gray-500 flex items-center">
										<AlertCircle className="h-3 w-3 inline mr-1" />
										Child of: <span className="font-mono ml-1">{quota.parent_quota_id}</span>
									</p>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default QuotasMonitorerListDisplay;